import { RecordSearch } from '../../service/records-search/models/record-search.model';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { RecordsSearchService } from '../../service/records-search/records-search.service';
import {
  AddSearchRecordToList,
  AddToFavorites,
  RemoveFromFavorites,
  RemoveSearchRecordFromList,
  ResetStore,
  SearchRecords,
} from './records-search.actions';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { append, iif, patch, updateItem } from '@ngxs/store/operators';
import { WishlistItem } from '../../../models/wishlist-item.model';
import { FavoritesFacade } from '../../../state/user/favorites/favorites.facade';
import { SearchResponse } from '../../service/search.model';
import { FiltersSearchState } from '../filters-search/filters-search.state';
import { AnalyticsService } from 'src/app/_core/analytics/analytics.service';

export interface RecordsSearchStateModel extends SearchResponse<RecordSearch> {
  query: string;
  order: string;
  isLoading: boolean;
}

export const initialState: RecordsSearchStateModel = {
  data: [],
  page: 0,
  size: 0,
  total: 0,
  query: '',
  order: 'relevance',
  isLoading: false,
};

@State<RecordsSearchStateModel>({
  name: 'searchRecords',
  defaults: initialState,
})
@Injectable()
export class RecordsSearchState {
  private static readonly MAX_ITEMS_PER_PAGE = 20;

  private readonly favoritesFacade = inject(FavoritesFacade);
  private readonly recordsSearchService = inject(RecordsSearchService);
  private readonly store = inject(Store);
  private readonly analyticsService = inject(AnalyticsService);

  @Selector()
  static records(state: RecordsSearchStateModel) {
    return state.data;
  }

  @Selector()
  static pagination(state: RecordsSearchStateModel) {
    return {
      page: state.page,
      size: state.size,
      total: state.total,
    };
  }

  @Selector()
  static isLoading(state: RecordsSearchStateModel) {
    return state.isLoading;
  }

  @Selector()
  static query(state: RecordsSearchStateModel) {
    return state.query;
  }

  @Action(AddSearchRecordToList)
  addSearchRecordToList(
    context: StateContext<RecordsSearchStateModel>,
    { userId, listId, recordId }: AddSearchRecordToList
  ): Observable<WishlistItem> {
    return this.recordsSearchService.addToList(userId, listId, recordId).pipe(
      tap((wishlistItem) => {
        context.setState(
          patch<RecordsSearchStateModel>({
            data: updateItem<RecordSearch>(
              (record) => record.id === recordId,
              patch<RecordSearch>({
                actions: ['REMOVE_FROM_LIST'],
                additionalData: patch({
                  elementWishListId: wishlistItem.id.toString(),
                }),
              })
            ),
          })
        );

        this.analyticsService.sendUserActionEvent('add_to_list', true);
      }),
      catchError((error) => {
        this.analyticsService.sendUserActionEvent('add_to_list', false);
        return throwError(() => error);
      })
    );
  }

  @Action(RemoveSearchRecordFromList)
  removeSearchRecordFromList(
    context: StateContext<RecordsSearchStateModel>,
    { userId, listId, recordId, elementWishListId }: RemoveSearchRecordFromList
  ): Observable<void> {
    return this.recordsSearchService.removeFromList(userId, listId, elementWishListId).pipe(
      tap(() => {
        context.setState(
          patch<RecordsSearchStateModel>({
            data: updateItem<RecordSearch>(
              (record) => record.id === recordId,
              patch<RecordSearch>({
                actions: ['ADD_TO_LIST'],
              })
            ),
          })
        );

        this.analyticsService.sendUserActionEvent('remove_from_list', true);
      }),
      catchError((error) => {
        this.analyticsService.sendUserActionEvent('remove_from_list', false);
        return throwError(() => error);
      })
    );
  }

  @Action(AddToFavorites)
  addToFavorites(context: StateContext<RecordsSearchStateModel>, { recordId }: AddToFavorites) {
    return this.favoritesFacade.postFavorite(recordId).pipe(
      tap(() => {
        context.setState(
          patch<RecordsSearchStateModel>({
            data: updateItem<RecordSearch>(
              (record) => record.id === recordId,
              patch<RecordSearch>({
                actions: ['REMOVE_FROM_FAVORITES'],
              })
            ),
          })
        );

        this.analyticsService.sendUserActionEvent('add_favorite', true);
      }),
      catchError((error) => {
        this.analyticsService.sendUserActionEvent('add_favorite', false);
        return throwError(() => error);
      })
    );
  }

  @Action(RemoveFromFavorites)
  removeFromFavorites(context: StateContext<RecordsSearchStateModel>, { recordId }: RemoveFromFavorites) {
    return this.favoritesFacade.deleteFavorite(recordId).pipe(
      tap(() => {
        context.setState(
          patch<RecordsSearchStateModel>({
            data: updateItem<RecordSearch>(
              (record) => record.id === recordId,
              patch<RecordSearch>({
                actions: ['ADD_TO_FAVORITES'],
              })
            ),
          })
        );

        this.analyticsService.sendUserActionEvent('remove_favorite', true);
      }),
      catchError((error) => {
        this.analyticsService.sendUserActionEvent('remove_favorite', false);
        return throwError(() => error);
      })
    );
  }

  @Action(SearchRecords)
  searchRecords(
    context: StateContext<RecordsSearchStateModel>,
    { page, query, additionalData, order, searchOrigin }: SearchRecords
  ): Observable<Omit<RecordsSearchStateModel, 'query' | 'order' | 'isLoading'>> {
    context.setState(
      patch<RecordsSearchStateModel>({
        isLoading: true,
      })
    );

    const activeFilters = this.store.selectSnapshot(FiltersSearchState.filters).flatMap((group) => {
      const selectedItem = group.filterItems.find((item) => item.selected);
      return selectedItem ? [{ name: group.value, value: selectedItem.value.trim() }] : [];
    });

    const filters = activeFilters.map((f) => `${f.name}:"${f.value}"`).join(';');

    const analyticsFacets = [
      ...activeFilters.map((f) => f.name),
      ...(additionalData?.criteriaFacets?.facetNames || []),
    ];

    return this.recordsSearchService
      .search(
        {
          limit: RecordsSearchState.MAX_ITEMS_PER_PAGE,
          offset: page * RecordsSearchState.MAX_ITEMS_PER_PAGE,
          query,
          order,
          filters,
        },
        additionalData
      )
      .pipe(
        tap((response) => {
          context.setState(
            patch<RecordsSearchStateModel>({
              data: iif(page > 0, append(response.data), response.data),
              page: response.page,
              size: response.size,
              total: response.total,
              query,
              order,
              isLoading: false,
            })
          );

          this.analyticsService.sendSearchPerformedEvent(analyticsFacets, response.data?.length > 0, searchOrigin);
        }),
        catchError((error) => {
          this.analyticsService.sendSearchPerformedEvent(analyticsFacets, false, searchOrigin);
          return throwError(() => error);
        })
      );
  }

  @Action(ResetStore)
  resetStore(context: StateContext<RecordsSearchStateModel>): void {
    context.setState(initialState);
  }
}
